import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QRScanner from '../components/QRScanner';
import { FaPlay, FaPause, FaStepForward, FaPlus, FaTimes, FaUsers, FaCopy, FaMusic, FaSearch, FaStop, FaVolumeUp, FaTrash } from 'react-icons/fa';
import '../styles/PartyPage.css';

const API = process.env.REACT_APP_API_URL || '';

function PartyPage({ user, playTrack: globalPlayTrack }) {
  const [myParties, setMyParties] = useState([]);
  const [activeParty, setActiveParty] = useState(null);
  const [partyData, setPartyData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [partyName, setPartyName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [message, setMessage] = useState(null);
  const [showAddTrack, setShowAddTrack] = useState(false);
  const [trackSearch, setTrackSearch] = useState('');
  const [trackResults, setTrackResults] = useState([]);

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [needsInteraction, setNeedsInteraction] = useState(false);
  const pollRef = useRef(null);
  const lastTrackIdRef = useRef(null);

  const isHost = partyData?.host_id === user?.id;

  useEffect(() => { loadMyParties(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  useEffect(() => {
    if (activeParty) {
      loadPartyData(activeParty);
      pollRef.current = setInterval(() => loadPartyData(activeParty), 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeParty]);

  const loadMyParties = async () => {
    try { const res = await axios.get(`${API}/api/party`); setMyParties(res.data); } catch (e) {}
  };

  const loadPartyData = async (id) => {
    try {
      const res = await axios.get(`${API}/api/party/${id}`);
      setPartyData(res.data);

      // Sync pour les non-hôtes
      if (res.data.host_id !== user?.id && audioRef.current && res.data.currentTrack) {
        const trackChanged = lastTrackIdRef.current !== res.data.current_track_id;

        if (trackChanged) {
          lastTrackIdRef.current = res.data.current_track_id;
          audioRef.current.src = getAudioUrl(res.data.currentTrack.file_path);
          audioRef.current.load();
          if (res.data.is_playing) {
            audioRef.current.currentTime = res.data.current_position || 0;
            audioRef.current.play().then(() => {
              setIsPlaying(true);
              setNeedsInteraction(false);
            }).catch(() => {
              setNeedsInteraction(true);
            });
          }
        } else if (res.data.is_playing && audioRef.current.paused && !needsInteraction) {
          audioRef.current.play().catch(() => setNeedsInteraction(true));
        } else if (!res.data.is_playing && !audioRef.current.paused) {
          audioRef.current.pause();
          setIsPlaying(false);
        }

        // Sync position si trop décalé
        if (res.data.is_playing && !audioRef.current.paused && Math.abs(audioRef.current.currentTime - (res.data.current_position || 0)) > 4) {
          audioRef.current.currentTime = res.data.current_position || 0;
        }
      }
    } catch (e) {}
  };

  const forcePlay = () => {
    if (audioRef.current && partyData?.currentTrack) {
      audioRef.current.src = getAudioUrl(partyData.currentTrack.file_path);
      audioRef.current.currentTime = partyData.current_position || 0;
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        setNeedsInteraction(false);
      }).catch(() => {});
    }
  };

  const getAudioUrl = (fp) => fp ? (fp.startsWith('http') ? fp : `${API}${fp.startsWith('/') ? fp : '/' + fp}`) : '';
  const getCoverUrl = (url) => url ? (url.startsWith('http') ? url : `${API}${url.startsWith('/') ? url : '/' + url}`) : null;

  const createParty = async () => {
    if (!partyName.trim()) return;
    try {
      const res = await axios.post(`${API}/api/party`, { name: partyName });
      setShowCreate(false); setPartyName('');
      setMessage({ type: 'success', text: `Party créée ! Code: ${res.data.code}` });
      loadMyParties(); setActiveParty(res.data.id);
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.error || 'Erreur' }); }
  };

  const joinParty = async (code) => {
    const c = code || joinCode;
    if (!c.trim()) return;
    try {
      const res = await axios.post(`${API}/api/party/join/${c}`);
      setShowJoin(false); setJoinCode(''); setShowScanner(false);
      setMessage({ type: 'success', text: `Vous avez rejoint "${res.data.name}" !` });
      loadMyParties(); setActiveParty(res.data.id);
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.error || 'Code invalide' }); }
  };

  const copyCode = () => {
    if (partyData?.code) {
      navigator.clipboard.writeText(partyData.code);
      setMessage({ type: 'success', text: 'Code copié !' });
      setTimeout(() => setMessage(null), 2000);
    }
  };

  // === HOST CONTROLS ===
  const hostPlayTrack = async (track) => {
    if (!isHost || !audioRef.current) return;
    audioRef.current.src = getAudioUrl(track.file_path);
    audioRef.current.load();
    audioRef.current.play().catch(() => {});
    setIsPlaying(true);
    lastTrackIdRef.current = track.track_id || track.id;
    await axios.put(`${API}/api/party/${activeParty}/state`, {
      current_track_id: track.track_id || track.id, current_position: 0, is_playing: true
    });
    // Refresh immédiat pour voir le now playing
    loadPartyData(activeParty);
  };

  const hostTogglePlay = async () => {
    if (!isHost || !audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
    else { audioRef.current.play().catch(() => {}); setIsPlaying(true); }
    await axios.put(`${API}/api/party/${activeParty}/state`, {
      current_track_id: partyData.current_track_id,
      current_position: audioRef.current.currentTime,
      is_playing: !isPlaying
    });
  };

  const hostNextTrack = async () => {
    if (!isHost || !partyData?.queue?.length) return;
    hostPlayTrack(partyData.queue[0]);
  };

  // Sync position hôte
  useEffect(() => {
    if (!isHost || !activeParty || !isPlaying) return;
    const iv = setInterval(async () => {
      if (audioRef.current && !audioRef.current.paused) {
        await axios.put(`${API}/api/party/${activeParty}/state`, {
          current_track_id: partyData?.current_track_id,
          current_position: audioRef.current.currentTime,
          is_playing: true
        }).catch(() => {});
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [isHost, activeParty, isPlaying, partyData?.current_track_id]);

  const endParty = async () => {
    if (!window.confirm('Terminer cette Listening Party ?')) return;
    try {
      await axios.put(`${API}/api/party/${activeParty}/end`);
      setActiveParty(null); setPartyData(null);
      if (audioRef.current) audioRef.current.pause();
      loadMyParties();
    } catch (e) {}
  };

  const searchTracks = async () => {
    if (trackSearch.length < 2) return;
    try {
      const res = await axios.get(`${API}/api/tracks/search`, { params: { q: trackSearch, limit: 10 } });
      setTrackResults(res.data);
    } catch (e) {}
  };

  const addTrackToQueue = async (track) => {
    try {
      await axios.post(`${API}/api/party/${activeParty}/queue`, { track_id: track.id });
      setMessage({ type: 'success', text: `"${track.title}" ajouté` });
      setTimeout(() => setMessage(null), 2000);
      loadPartyData(activeParty);
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.error || 'Erreur' }); }
  };

  const removeFromQueue = async (queueId) => {
    try {
      await axios.delete(`${API}/api/party/${activeParty}/queue/${queueId}`);
      loadPartyData(activeParty);
    } catch (e) {}
  };

  const moveInQueue = async (index, direction) => {
    if (!partyData?.queue) return;
    const q = [...partyData.queue];
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= q.length) return;
    [q[index], q[newIdx]] = [q[newIdx], q[index]];
    const order = q.map(item => item.id);
    try {
      await axios.put(`${API}/api/party/${activeParty}/queue/reorder`, { order });
      loadPartyData(activeParty);
    } catch (e) {}
  };

  const clearQueue = async () => {
    if (!window.confirm("Vider la file d attente ?")) return;
    try {
      await axios.delete(`${API}/api/party/${activeParty}/queue`);
      loadPartyData(activeParty);
    } catch (e) {}
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => { setCurrentTime(audio.currentTime); setDuration(audio.duration || 0); };
    const onEnded = () => { if (isHost) hostNextTrack(); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('ended', onEnded); };
  }, [isHost, partyData?.queue]);

  const formatTime = (s) => isNaN(s) ? '0:00' : `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`;

  return (
    <div className="party-page">
      <audio ref={audioRef} />

      {!activeParty ? (
        <>
          <div className="party-hero">
            <h1>📻 Listening Party</h1>
            <p>Écoutez de la musique ensemble en temps réel</p>
            <div className="party-hero-actions">
              <button className="party-btn primary" onClick={() => setShowCreate(true)}><FaPlus /> Créer une Party</button>
              <button className="party-btn secondary" onClick={() => setShowJoin(true)}><FaUsers /> Rejoindre</button>
              <button className="party-btn secondary" onClick={() => setShowScanner(true)}>📷 Scanner QR</button>
            </div>
          </div>

          {showCreate && (
            <div className="party-modal">
              <h3>Créer une Listening Party</h3>
              <input value={partyName} onChange={e => setPartyName(e.target.value)} placeholder="Nom de la party..."
                onKeyDown={e => e.key === 'Enter' && createParty()} />
              <div className="party-modal-actions">
                <button onClick={createParty}>Créer</button>
                <button className="cancel" onClick={() => setShowCreate(false)}>Annuler</button>
              </div>
            </div>
          )}

          {showJoin && (
            <div className="party-modal">
              <h3>Rejoindre une Party</h3>
              <input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} placeholder="CODE..." maxLength={8}
                onKeyDown={e => e.key === 'Enter' && joinParty()} />
              <div className="party-modal-actions">
                <button onClick={() => joinParty()}>Rejoindre</button>
                <button className="cancel" onClick={() => setShowJoin(false)}>Annuler</button>
              </div>
            </div>
          )}

          {myParties.length > 0 && (
            <div className="my-parties">
              <h2>Mes Parties actives</h2>
              {myParties.map(p => (
                <div key={p.id} className="party-card" onClick={() => setActiveParty(p.id)}>
                  <div className="party-card-info">
                    <div className="party-card-name">{p.name}</div>
                    <div className="party-card-meta">Hôte: {p.host_name} • {p.member_count} participants • Code: {p.code}</div>
                  </div>
                  <FaPlay className="party-card-join" />
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="party-active">
          <div className="party-top-bar">
            <button className="party-back" onClick={() => { setActiveParty(null); setPartyData(null); if (audioRef.current) audioRef.current.pause(); }}>← Retour</button>
            <div className="party-top-info">
              <h2>{partyData?.name || 'Chargement...'}</h2>
              <div className="party-code-row">
                <span className="party-code" onClick={copyCode}>{partyData?.code} <FaCopy /></span>
                {partyData?.code && <img className="party-qr" src={"https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=" + encodeURIComponent(window.location.origin + "/party?join=" + partyData.code)} alt="QR" />}
              </div>
            </div>
            {isHost && <button className="party-end-btn" onClick={endParty}><FaStop /> Terminer</button>}
          </div>

          <div className="party-layout">
            {/* Now playing */}
            <div className="party-now-playing">
              {partyData?.currentTrack ? (
                <>
                  {partyData.currentTrack.cover_url && (
                    <img src={getCoverUrl(partyData.currentTrack.cover_url)} alt="" className="party-cover" />
                  )}
                  <div className="party-track-info">
                    <div className="party-track-title">{partyData.currentTrack.title}</div>
                    <div className="party-track-artist">{partyData.currentTrack.artist}</div>
                    <div className="party-track-album">{partyData.currentTrack.album_title}</div>
                  </div>
                  <div className="party-progress">
                    <span>{formatTime(currentTime)}</span>
                    <div className="party-progress-bar">
                      <div className="party-progress-fill" style={{ width: duration > 0 ? `${(currentTime/duration)*100}%` : '0%' }} />
                    </div>
                    <span>{formatTime(duration)}</span>
                  </div>

                  {isHost ? (
                    <div className="party-controls">
                      <button onClick={hostTogglePlay}>{isPlaying ? <FaPause /> : <FaPlay />}</button>
                      <button onClick={hostNextTrack} disabled={!partyData?.queue?.length}><FaStepForward /></button>
                    </div>
                  ) : (
                    <div className="party-listener-zone">
                      {needsInteraction ? (
                        <button className="party-sync-btn" onClick={forcePlay}>
                          <FaVolumeUp /> Écouter en direct
                        </button>
                      ) : (
                        <div className="party-sync-info">{isPlaying ? '🔴 En direct' : '⏸ En pause'}</div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="party-no-track">
                  <FaMusic className="party-no-track-icon" />
                  <p>{isHost ? 'Cliquez sur une piste dans la file pour lancer la lecture !' : "En attente de l'hôte..."}</p>
                  {!isHost && partyData?.is_playing && (
                    <button className="party-sync-btn" onClick={forcePlay}>
                      <FaVolumeUp /> Écouter en direct
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="party-sidebar">
              <div className="party-members">
                <h3><FaUsers /> Participants ({partyData?.members?.length || 0})</h3>
                {partyData?.members?.map(m => (
                  <div key={m.id} className="party-member">
                    <div className="party-member-avatar">{(m.display_name || m.username).charAt(0).toUpperCase()}</div>
                    <span>{m.display_name || m.username}</span>
                    {m.id === partyData.host_id && <span className="host-badge">Hôte</span>}
                  </div>
                ))}
              </div>

              <div className="party-queue">
                <div className="party-queue-header">
                  <h3><FaMusic /> File d'attente ({partyData?.queue?.length || 0})</h3>
                  <div className="queue-header-actions">
                    {isHost && partyData?.queue?.length > 0 && (
                      <button className="queue-clear-btn" onClick={clearQueue} title="Vider la file"><FaTrash /></button>
                    )}
                    <button className="add-track-btn" onClick={() => setShowAddTrack(!showAddTrack)}><FaPlus /></button>
                  </div>
                </div>

                {showAddTrack && (
                  <div className="party-search">
                    <div className="party-search-bar">
                      <FaSearch />
                      <input value={trackSearch} onChange={e => setTrackSearch(e.target.value)}
                        placeholder="Chercher une piste..."
                        onKeyDown={e => e.key === 'Enter' && searchTracks()} />
                    </div>
                    {trackResults.map(t => (
                      <div key={t.id} className="party-search-result" onClick={() => addTrackToQueue(t)}>
                        <FaPlus className="add-icon" />
                        <div>
                          <div className="search-result-title">{t.title}</div>
                          <div className="search-result-artist">{t.artist || t.album_artist} — {t.album_title}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="queue-list">
                  {partyData?.queue?.map((t, i) => (
                    <div key={t.id} className={`queue-item ${i === 0 ? 'next' : ''}`}>
                      <span className="queue-pos">{i + 1}</span>
                      {t.cover_url && <img src={getCoverUrl(t.cover_url)} alt="" className="queue-cover" />}
                      <div className="queue-track-info" onClick={() => isHost && hostPlayTrack(t)}>
                        <div className="queue-track-title">{t.title}</div>
                        <div className="queue-track-artist">{t.artist} • par {t.added_by_name}</div>
                      </div>
                      <span className="queue-track-dur">{formatTime(t.duration)}</span>
                      {isHost && (
                        <div className="queue-controls">
                          <button className="queue-ctrl" onClick={() => moveInQueue(i, -1)} disabled={i === 0} title="Monter">▲</button>
                          <button className="queue-ctrl" onClick={() => moveInQueue(i, 1)} disabled={i === partyData.queue.length - 1} title="Descendre">▼</button>
                          <button className="queue-ctrl queue-play" onClick={() => hostPlayTrack(t)} title="Jouer maintenant"><FaPlay /></button>
                          <button className="queue-ctrl queue-remove" onClick={() => removeFromQueue(t.id)} title="Retirer"><FaTimes /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {(!partyData?.queue || partyData.queue.length === 0) && !showAddTrack && (
                  <div className="queue-empty">La file est vide — ajoutez des pistes !</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {message && <div className={`party-message ${message.type}`}>{message.text}</div>}
      {showScanner && <QRScanner onScan={(code) => joinParty(code)} onClose={() => setShowScanner(false)} />}
    </div>
  );
}

export default PartyPage;
